import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { optionalInput } from '@/common/form/optionalInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { Input } from '@/common/components/ui/input';
import { Label } from '@/common/components/ui/label';
import { userQueryKeys } from '@/features/auth/queries';
import { AccountService } from '@/features/auth/services/AccountService';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';

const personalInfoSchema = z.object({
  firstName: optionalInput(z.string()).transform((v) => v ?? ''),
  lastName: optionalInput(z.string()).transform((v) => v ?? ''),
  phone: optionalInput(z.string()).transform((v) => v ?? ''),
});

export function PersonalInfoTab() {
  const user = useCurrentUser();
  const queryClient = useQueryClient();

  const form = useAppForm(
    appFormOptions({
      schema: personalInfoSchema,
      defaultValues: {
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        phone: user.phone ?? null,
      },
      onSubmit: async ({ value }) => {
        await AccountService.updateProfile(value);
        await queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
        toast.success(t`Profile updated successfully`);
      },
    })
  );

  return (
    <div className="max-w-xl">
      <div className="rounded-2xl border border-border bg-card p-6">
        <form.AppForm>
          <form.FormElement>
            <div className="space-y-4">
              <FormErrors />

              <div className="grid grid-cols-2 gap-4">
                <form.AppField name="firstName">
                  {(field) => (
                    <field.TextField label={<Trans>First name</Trans>} placeholder="Jan" />
                  )}
                </form.AppField>
                <form.AppField name="lastName">
                  {(field) => (
                    <field.TextField label={<Trans>Last name</Trans>} placeholder="Novák" />
                  )}
                </form.AppField>
              </div>

              <form.AppField name="phone">
                {(field) => (
                  <field.TextField
                    label={<Trans>Phone</Trans>}
                    placeholder="+420 777 000 000"
                    type="tel"
                  />
                )}
              </form.AppField>

              <div className="flex flex-col gap-1.5">
                <Label className="text-muted-foreground">
                  <Trans>Email</Trans>
                </Label>
                <Input value={user.email} disabled readOnly />
              </div>

              <FormSubmit>
                <Trans>Save changes</Trans>
              </FormSubmit>
            </div>
          </form.FormElement>
        </form.AppForm>
      </div>
    </div>
  );
}
